import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, FileText, Clock, CheckCircle, AlertCircle, FileCheck, Receipt, Users, Plane, Upload, ClipboardCheck, Settings, Calendar, Wallet, ArrowRight, Mail, PawPrint, BarChart3, RefreshCcw, Link2, Activity, Sliders, ListChecks, Send, Eye, EyeOff, PenLine, Bell, GraduationCap, Folder, BookOpen } from 'lucide-react';
import { logger } from '../../utils/logger';
import { DataHealthSweepModal } from '../../components/admin/DataHealthSweepModal';
import { PayCycleDetailModal } from '../../components/admin/PayCycleDetailModal';
import { RevenueDetailModal } from '../../components/admin/RevenueDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followUps, setFollowUps] = useState(null);
  const [reminderSendingId, setReminderSendingId] = useState(null);
  const [clearingBalanceId, setClearingBalanceId] = useState(null);
  const [cycleModalOpen, setCycleModalOpen] = useState(false);
  const [revenueModalWindow, setRevenueModalWindow] = useState(null); // 'ytd' | 'all' | null
  const [recalcRunning, setRecalcRunning] = useState(false);
  const [dataHealthOpen, setDataHealthOpen] = useState(false);
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
    fetchFollowUps();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      }
    } catch (err) {
      logger.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowUps = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard/follow-ups?days=3&limit=10`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (res.ok) setFollowUps(await res.json());
    } catch (err) {
      logger.error('Failed to fetch follow-ups:', err);
    }
  };

  const [trainingStats, setTrainingStats] = useState(null);
  useEffect(() => {
    if (!password) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/vet-clinics/stats`, {
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
        });
        if (res.ok) setTrainingStats(await res.json());
      } catch (e) { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    navigate('/admin/login');
  };

  const handleRecalcValidUntil = async () => {
    if (recalcRunning) return;
    if (!window.confirm(
      'This will recalculate the "Valid Until" date on every quote so it is exactly 30 days after the quote date. Continue?'
    )) return;
    setRecalcRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrations/quotes/recalc-valid-until`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Migration failed');
      alert(
        `Valid-until dates recalculated.\n\n` +
        `Quotes scanned: ${data.scanned}\n` +
        `Updated: ${data.updated}\n` +
        `Already correct: ${data.skipped}\n` +
        (data.errors?.length ? `Errors: ${data.errors.join(', ')}` : '')
      );
    } catch (err) {
      logger.error('recalc valid_until failed:', err);
      alert(`Failed: ${err.message}`);
    } finally {
      setRecalcRunning(false);
    }
  };

  const handleSendReminder = async (quoteRow, e) => {
    e.stopPropagation();
    const ok = window.confirm(
      `Send payment reminder to ${quoteRow.client_name} (${quoteRow.client_email})?\n\n` +
      `${quoteRow.quote_number} — Balance due: ${quoteRow.currency}${quoteRow.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    );
    if (!ok) return;
    setReminderSendingId(quoteRow.id);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/quotes/${quoteRow.id}/send-payment-reminder`,
        {
          method: 'POST',
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
        },
      );
      if (response.ok) {
        alert(`Payment reminder sent to ${quoteRow.client_email}.`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to send reminder: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Send reminder error:', err);
      alert('Failed to send reminder. Please try again.');
    } finally {
      setReminderSendingId(null);
    }
  };

  // Send a balance reminder against a standalone receipt (Outstanding Receipts widget)
  const handleSendReceiptReminder = async (receiptRow, e) => {
    e.stopPropagation();
    const ok = window.confirm(
      `Send balance reminder to ${receiptRow.client_name} (${receiptRow.client_email})?\n\n` +
      `Receipt ${receiptRow.latest_receipt_number} — Balance still owed: ${receiptRow.currency}${(receiptRow.total_owed || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    );
    if (!ok) return;
    setReminderSendingId(receiptRow.latest_receipt_id);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/receipts/${receiptRow.latest_receipt_id}/send-balance-reminder`,
        {
          method: 'POST',
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
        },
      );
      if (response.ok) {
        alert(`Balance reminder sent to ${receiptRow.client_email}.`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to send reminder: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Send receipt reminder error:', err);
      alert('Failed to send reminder. Please try again.');
    } finally {
      setReminderSendingId(null);
    }
  };

  // One-click "Mark as Paid" — zero out the outstanding balance on a standalone receipt.
  const handleClearReceiptBalance = async (receiptRow, e) => {
    e.stopPropagation();
    const ok = window.confirm(
      `Mark receipt ${receiptRow.latest_receipt_number} as fully paid?\n\n` +
      `Client: ${receiptRow.client_name}\n` +
      `Outstanding balance to clear: ${receiptRow.currency}${(receiptRow.total_owed || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n` +
      `This will remove it from the Outstanding Receipts list.`
    );
    if (!ok) return;
    setClearingBalanceId(receiptRow.latest_receipt_id);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/receipts/${receiptRow.latest_receipt_id}/clear-balance`,
        {
          method: 'POST',
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
        },
      );
      if (response.ok) {
        await fetchStats();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed to clear balance: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Clear receipt balance error:', err);
      alert('Failed to clear balance. Please try again.');
    } finally {
      setClearingBalanceId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_pet-relocation-hub/artifacts/87vqpckq_Pawsome%20Logo.jpg" 
                alt="Pawsome Ventures" 
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-primary truncate">Pawsome Ventures Admin</h1>
                <p className="text-xs sm:text-sm text-foreground/60 truncate">Invoice Management System</p>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 sm:gap-4">
              <a href="/" target="_blank" className="text-sm text-primary hover:text-primary/80">
                View Website
              </a>
              <button
                onClick={handleRecalcValidUntil}
                disabled={recalcRunning}
                data-testid="recalc-valid-until-btn"
                title="Recalculate every quote's Valid Until date to exactly 30 days after its quote date"
                className="px-3 py-2 text-xs border border-amber-300 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-60 flex items-center gap-1.5"
              >
                <Calendar className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{recalcRunning ? 'Fixing…' : 'Fix Quote Expiry Dates'}</span>
                <span className="sm:hidden">{recalcRunning ? 'Fixing…' : 'Fix Expiry'}</span>
              </button>
              <button
                onClick={handleLogout}
                className="px-3 sm:px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">{stats?.total_invoices || 0}</h3>
            <p className="text-xs sm:text-sm text-foreground/60">Total Invoices</p>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">{stats?.pending_invoices || 0}</h3>
            <p className="text-xs sm:text-sm text-foreground/60">Pending Payment</p>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">{stats?.paid_invoices || 0}</h3>
            <p className="text-xs sm:text-sm text-foreground/60">Paid Invoices</p>
          </div>

          <button
            type="button"
            onClick={() => setRevenueModalWindow('all')}
            className="bg-white rounded-xl p-4 sm:p-6 border border-border shadow-sm col-span-2 lg:col-span-1 text-left hover:border-primary/50 transition-colors"
            data-testid="total-revenue-card"
            title="See every payment that makes up this total"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-foreground mb-1">CI${(stats?.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            <p className="text-sm text-foreground/60">Total Revenue</p>
            {(stats?.invoice_revenue != null || stats?.quote_payment_revenue != null || stats?.receipt_revenue != null) && (
              <div className="mt-3 pt-3 border-t border-border text-xs text-foreground/60 space-y-0.5">
                <div className="flex justify-between">
                  <span>Paid invoices</span>
                  <span className="font-medium text-foreground/80">CI${(stats?.invoice_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span>Deposits &amp; quote payments</span>
                  <span className="font-medium text-foreground/80">CI${(stats?.quote_payment_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                {stats?.receipt_revenue > 0 && (
                  <div className="flex justify-between" data-testid="receipt-revenue-row">
                    <span>Manual receipts</span>
                    <span className="font-medium text-foreground/80">CI${(stats?.receipt_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            )}
          </button>
        </div>

        {/* Revenue snapshot — YTD + current pay cycle side by side */}
        <div
          className="grid grid-cols-2 gap-3 sm:gap-6 mb-6 sm:mb-8"
          data-testid="revenue-snapshot-row"
        >
          <button
            type="button"
            onClick={() => setRevenueModalWindow('ytd')}
            className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 sm:p-6 border-2 border-emerald-200 shadow-sm text-left hover:border-emerald-400 transition-colors"
            data-testid="ytd-revenue-card"
            title="See every payment year-to-date, grouped by month"
          >
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              <span className="text-[11px] sm:text-xs uppercase tracking-wider font-bold text-emerald-700">
                Year to Date
              </span>
            </div>
            <div className="text-2xl sm:text-4xl font-bold text-emerald-700">
              CI${(stats?.ytd_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] sm:text-xs text-foreground/60 mt-1">
              {new Date().getFullYear()} · net of refunds
            </p>
            <div className="mt-2 pt-2 border-t border-emerald-100 flex items-baseline justify-between gap-2" data-testid="ytd-net-income">
              <span className="text-[10px] sm:text-xs uppercase tracking-wider font-semibold text-purple-800/70">
                Net Income
              </span>
              <span className={`text-sm sm:text-lg font-bold ${(stats?.ytd_net_income || 0) < 0 ? 'text-red-700' : 'text-purple-800'}`}>
                CI${(stats?.ytd_net_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </button>

          <button
            onClick={() => setCycleModalOpen(true)}
            className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 sm:p-6 border-2 border-blue-200 shadow-sm text-left hover:border-blue-400 transition-colors"
            data-testid="current-cycle-card"
            title="See which payments make up this cycle"
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-[11px] sm:text-xs uppercase tracking-wider font-bold text-blue-700">
                Current Pay Cycle
              </span>
            </div>
            <div className="text-2xl sm:text-4xl font-bold text-blue-700">
              CI${(stats?.current_cycle_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] sm:text-xs text-foreground/60 mt-1 truncate">
              {stats?.current_cycle_label || '—'}
            </p>
            <div className="mt-2 pt-2 border-t border-blue-100 flex items-baseline justify-between gap-2" data-testid="cycle-net-income">
              <span className="text-[10px] sm:text-xs uppercase tracking-wider font-semibold text-purple-800/70">
                Net Income
              </span>
              <span className={`text-sm sm:text-lg font-bold ${(stats?.current_cycle_net_income || 0) < 0 ? 'text-red-700' : 'text-purple-800'}`}>
                CI${(stats?.current_cycle_net_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </button>
        </div>

        {/* Follow-Up Nudges — two side-by-side lists of stale quotes */}
        {followUps && (followUps.not_opened.length > 0 || followUps.not_signed.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8" data-testid="follow-up-nudges">
            <FollowUpList
              testId="follow-ups-not-opened"
              title="Not opened yet"
              subtitle={`Quotes emailed ${followUps.days}+ days ago that the recipient hasn't opened`}
              accent="amber"
              Icon={EyeOff}
              items={followUps.not_opened}
              navigate={navigate}
            />
            <FollowUpList
              testId="follow-ups-not-signed"
              title="Not signed yet"
              subtitle={`Quotes emailed ${followUps.days}+ days ago that haven't been accepted`}
              accent="rose"
              Icon={PenLine}
              items={followUps.not_signed}
              navigate={navigate}
            />
          </div>
        )}

        {/* Outstanding Balances Widget */}
        {stats?.outstanding_quotes_count > 0 && (
          <div
            className="bg-white rounded-xl border border-border shadow-sm mb-8 overflow-hidden"
            data-testid="outstanding-balances-widget"
          >
            <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-amber-50 to-white flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Outstanding Balances</h2>
                  <p className="text-sm text-foreground/60">
                    {stats.outstanding_quotes_count} quote{stats.outstanding_quotes_count === 1 ? '' : 's'} with partial payments
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-xs text-foreground/60 uppercase tracking-wide">Collected</p>
                  <p className="text-lg font-semibold text-green-700" data-testid="deposits-received-total">
                    CI${(stats.deposits_received_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-foreground/60 uppercase tracking-wide">Still Owed</p>
                  <p className="text-2xl font-bold text-amber-700" data-testid="outstanding-balance-total">
                    CI${(stats.outstanding_balance_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Quote</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Client</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Paid</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Balance Due</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {stats.outstanding_quotes.map((q) => (
                    <tr
                      key={q.id}
                      className="hover:bg-amber-50/40 cursor-pointer transition-colors"
                      data-testid={`outstanding-row-${q.id}`}
                      onClick={() => navigate(`/admin/quotes/${q.id}`)}
                    >
                      <td className="px-6 py-3 font-mono font-semibold text-primary">{q.quote_number}</td>
                      <td className="px-6 py-3 text-foreground">{q.client_name}</td>
                      <td className="px-6 py-3 text-right text-foreground/80">{q.currency}{q.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-3 text-right text-green-700">
                        {q.currency}{q.paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {q.percent_paid != null && (
                          <span className="text-xs text-foreground/50 ml-1">({Math.round(q.percent_paid)}%)</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-amber-700">{q.currency}{q.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => handleSendReminder(q, e)}
                            disabled={reminderSendingId === q.id}
                            data-testid={`send-reminder-${q.id}`}
                            title="Send payment reminder email with quote PDF attached"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-amber-500 text-amber-700 rounded hover:bg-amber-100 transition-colors disabled:opacity-50"
                          >
                            <Mail className="w-3 h-3" />
                            {reminderSendingId === q.id ? 'Sending…' : 'Remind'}
                          </button>
                          <ArrowRight className="w-4 h-4 text-foreground/40" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Admin maintenance actions row */}
        <div className="mb-4 flex items-center justify-end gap-2" data-testid="admin-maintenance-row">
          <button
            onClick={() => setDataHealthOpen(true)}
            data-testid="data-health-sweep-btn"
            className="px-3 py-1.5 text-xs font-medium border border-border rounded-full bg-white hover:bg-gray-50 transition-colors flex items-center gap-1.5 text-foreground/80"
            title="Scan quotes/invoices/receipts for legacy-data drift (missing valid_until, naive datetimes, inverse-orphan receipts) and back-fill safely."
          >
            <Activity className="w-3 h-3" />
            Data Health Sweep
          </button>
        </div>

        {/* Standalone Receipts pill — quick jump to receipts not yet linked to invoice/quote */}
        {stats?.standalone_receipts_count > 0 && (
          <div
            className="mb-4 flex items-center justify-between gap-4 px-4 py-3 rounded-xl border border-red-200 bg-red-50 flex-wrap"
            data-testid="standalone-receipts-widget"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Link2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-red-900">
                  {stats.standalone_receipts_count} standalone receipt{stats.standalone_receipts_count === 1 ? '' : 's'} not linked to a quote or invoice
                </p>
                <p className="text-xs text-red-700/80">
                  Review these to catch ledger drift early — one click to attach them to the right quote.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/receipts?filter=standalone')}
              data-testid="standalone-receipts-review-btn"
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              Review →
            </button>
          </div>
        )}

        {/* Outstanding Receipts Widget — manually-created receipts with non-zero "amount still owed" */}
        {stats?.outstanding_receipts_count > 0 && (
          <div
            className="bg-white rounded-xl border border-border shadow-sm mb-8 overflow-hidden"
            data-testid="outstanding-receipts-widget"
          >
            <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-orange-50 to-white flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Outstanding Receipts</h2>
                  <p className="text-sm text-foreground/60">
                    {stats.outstanding_receipts_count} client{stats.outstanding_receipts_count === 1 ? '' : 's'} with partially-paid receipts
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-foreground/60 uppercase tracking-wide">Total Still Owed</p>
                <p className="text-2xl font-bold text-orange-700" data-testid="outstanding-receipts-total">
                  CI${(stats.outstanding_receipts_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Latest Receipt</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-foreground/60 uppercase"># Receipts</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Paid So Far</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Still Owed</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {stats.outstanding_receipts.map((r) => (
                    <tr
                      key={r.latest_receipt_id}
                      className="hover:bg-orange-50/40 cursor-pointer transition-colors"
                      data-testid={`outstanding-receipt-row-${r.latest_receipt_id}`}
                      onClick={() => navigate(`/admin/receipts/${r.latest_receipt_id}`)}
                    >
                      <td className="px-6 py-3">
                        <div className="font-semibold text-foreground">{r.client_name}</div>
                        <div className="text-xs text-foreground/50">{r.client_email}</div>
                      </td>
                      <td className="px-6 py-3 font-mono font-semibold text-primary">
                        {r.latest_receipt_number}
                      </td>
                      <td className="px-6 py-3 text-center text-foreground/80">{r.receipts_count}</td>
                      <td className="px-6 py-3 text-right text-green-700">
                        {r.currency}{(r.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-orange-700">
                        {r.currency}{(r.total_owed || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => handleClearReceiptBalance(r, e)}
                            disabled={clearingBalanceId === r.latest_receipt_id}
                            data-testid={`clear-receipt-balance-${r.latest_receipt_id}`}
                            title="Mark this receipt as fully paid (sets outstanding balance to 0)"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-green-600 text-green-700 rounded hover:bg-green-100 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle className="w-3 h-3" />
                            {clearingBalanceId === r.latest_receipt_id ? 'Clearing…' : 'Mark Paid'}
                          </button>
                          <button
                            onClick={(e) => handleSendReceiptReminder(r, e)}
                            disabled={reminderSendingId === r.latest_receipt_id}
                            data-testid={`send-receipt-reminder-${r.latest_receipt_id}`}
                            title="Send a friendly balance reminder email with the receipt PDF attached"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-orange-500 text-orange-700 rounded hover:bg-orange-100 transition-colors disabled:opacity-50"
                          >
                            <Mail className="w-3 h-3" />
                            {reminderSendingId === r.latest_receipt_id ? 'Sending…' : 'Remind'}
                          </button>
                          <ArrowRight className="w-4 h-4 text-foreground/40" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Training — pane for the Vet Training programme (flyer + email
            outreach to clinics). Kept as its own section so it doesn't get
            lost in the busier Quick Actions grid. */}
        <div
          className="bg-gradient-to-br from-teal-50 to-white rounded-xl border-2 border-teal-200 shadow-sm mb-6 sm:mb-8 overflow-hidden"
          data-testid="training-pane"
        >
          <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-teal-100 flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-foreground">Training</h2>
                <p className="text-xs sm:text-sm text-foreground/60">
                  60-minute in-clinic front-desk training for veterinary teams
                </p>
              </div>
            </div>
          </div>

          {/* Stats strip — only shown once we have any clinics tracked */}
          {trainingStats && trainingStats.total > 0 && (
            <div
              className="px-4 sm:px-5 pt-4 grid grid-cols-3 sm:grid-cols-6 gap-2"
              data-testid="training-stats"
            >
              {[
                { key: 'total',         label: 'Clinics',   accent: 'text-foreground',  value: trainingStats.total },
                { key: 'not_contacted', label: 'Untouched', accent: 'text-foreground/70', value: trainingStats.by_status.not_contacted },
                { key: 'emailed',       label: 'Emailed',   accent: 'text-blue-700',    value: trainingStats.by_status.emailed },
                { key: 'replied',       label: 'Replied',   accent: 'text-amber-700',   value: trainingStats.by_status.replied },
                { key: 'booked',        label: 'Booked',    accent: 'text-emerald-700', value: trainingStats.by_status.booked },
                { key: 'completed',     label: 'Completed', accent: 'text-primary',     value: trainingStats.by_status.completed },
              ].map((s) => (
                <div
                  key={s.key}
                  data-testid={`training-stat-${s.key}`}
                  className="bg-white/70 rounded-lg border border-teal-100 px-3 py-2"
                >
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-foreground/50">{s.label}</div>
                  <div className={`text-xl font-bold ${s.accent}`}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 sm:p-5">
            <a
              href={`${API_URL}/api/brand/vet-training-flyer.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="training-preview-flyer"
              className="flex items-center gap-3 p-4 border-2 border-primary/30 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm sm:text-base font-semibold text-foreground">Preview flyer</h3>
                <p className="text-xs text-foreground/60">Open the print-ready PDF</p>
              </div>
              <ArrowRight className="w-4 h-4 text-foreground/40 group-hover:text-primary transition-colors" />
            </a>
            <button
              type="button"
              onClick={() => navigate('/admin/vet-training-flyer/edit')}
              data-testid="training-edit-flyer"
              className="flex items-center gap-3 p-4 border-2 border-lavender/60 text-purple-700 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors group bg-white"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sliders className="w-5 h-5 text-purple-700" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <h3 className="text-sm sm:text-base font-semibold">Edit flyer</h3>
                <p className="text-xs opacity-80">Change wording, prices, benefits</p>
              </div>
              <ArrowRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/email-composer?template=training')}
              data-testid="training-email-clinic"
              className="flex items-center gap-3 p-4 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-white/20 transition-colors">
                <Send className="w-5 h-5 text-primary group-hover:text-white transition-colors" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <h3 className="text-sm sm:text-base font-semibold">Email a clinic</h3>
                <p className="text-xs opacity-80 group-hover:opacity-100">Compose with the flyer pre-attached</p>
              </div>
              <ArrowRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/vet-clinics')}
              data-testid="training-manage-clinics"
              className="flex items-center gap-3 p-4 border-2 border-teal-500 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-teal-700" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <h3 className="text-sm sm:text-base font-semibold">Manage clinics</h3>
                <p className="text-xs opacity-80">Address book + outreach log</p>
              </div>
              <ArrowRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>

        {/* Client Guides — a small pane pointing at the shareable relocation
            guides (starting with Cayman→UK). Same shape as the Training pane
            so it reads consistently. */}
        <div
          className="bg-gradient-to-br from-purple-50 to-white rounded-xl border-2 border-purple-200 shadow-sm mb-6 sm:mb-8 overflow-hidden"
          data-testid="client-guides-pane"
        >
          <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-purple-100 flex items-center gap-3">
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Plane className="w-6 h-6 text-purple-700" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-foreground">Client Guides</h2>
              <p className="text-xs sm:text-sm text-foreground/60">
                Ready-to-send relocation guides — PDF + shareable web page
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 sm:p-5">
            <a
              href={`${API_URL}/api/brand/cayman-to-uk-guide.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="uk-guide-preview-pdf"
              className="flex items-center gap-3 p-4 border-2 border-primary/30 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-primary" /></div>
              <div className="min-w-0 flex-1"><h3 className="text-sm sm:text-base font-semibold">Preview PDF</h3><p className="text-xs text-foreground/60">Cayman → UK, 3 pages</p></div>
              <ArrowRight className="w-4 h-4 text-foreground/40" />
            </a>
            <a
              href="/guides/cayman-to-uk"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="uk-guide-open-web"
              className="flex items-center gap-3 p-4 border-2 border-purple-400 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"><Link2 className="w-5 h-5 text-purple-700" /></div>
              <div className="min-w-0 flex-1 text-left"><h3 className="text-sm sm:text-base font-semibold">Web page</h3><p className="text-xs opacity-80">Shareable link — no download needed</p></div>
              <ArrowRight className="w-4 h-4 opacity-60" />
            </a>
            <button
              type="button"
              onClick={() => navigate('/admin/guides/cayman-to-uk')}
              data-testid="uk-guide-edit"
              className="flex items-center gap-3 p-4 border-2 border-lavender/60 text-purple-700 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors group bg-white"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"><Sliders className="w-5 h-5 text-purple-700" /></div>
              <div className="min-w-0 flex-1 text-left"><h3 className="text-sm sm:text-base font-semibold">Edit guide</h3><p className="text-xs opacity-80">Wording, timeline, brokers</p></div>
              <ArrowRight className="w-4 h-4 opacity-60" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/email-composer?template=uk-guide')}
              data-testid="uk-guide-email-client"
              className="flex items-center gap-3 p-4 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Send className="w-5 h-5 text-primary group-hover:text-white transition-colors" /></div>
              <div className="min-w-0 flex-1 text-left"><h3 className="text-sm sm:text-base font-semibold">Send to client</h3><p className="text-xs opacity-80">Compose with guide pre-attached</p></div>
              <ArrowRight className="w-4 h-4 opacity-60" />
            </button>
          </div>

          {/* Benefits & Scope flyer — attach to enquiry replies */}
          <div className="px-4 sm:px-5 pt-1 pb-4 sm:pb-5">
            <div className="border-t border-purple-100 pt-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-2">Enquiry response flyer</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <a
                  href={`${API_URL}/api/brand/benefits-scope-flyer.pdf`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="benefits-preview-pdf"
                  className="flex items-center gap-3 p-4 border-2 border-primary/30 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors bg-white"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-primary" /></div>
                  <div className="min-w-0 flex-1"><h3 className="text-sm font-semibold">Preview PDF</h3><p className="text-xs text-foreground/60">Benefits &amp; Scope, 1 page</p></div>
                </a>
                <button
                  type="button"
                  onClick={() => navigate('/admin/flyers/benefits-scope')}
                  data-testid="benefits-edit"
                  className="flex items-center gap-3 p-4 border-2 border-lavender/60 text-purple-700 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors bg-white"
                >
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"><Sliders className="w-5 h-5 text-purple-700" /></div>
                  <div className="min-w-0 flex-1 text-left"><h3 className="text-sm font-semibold">Edit flyer</h3><p className="text-xs opacity-80">Benefits, scope, wording</p></div>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin/email-composer?template=enquiry-reply-imports')}
                  data-testid="benefits-send-reply-imports"
                  className="flex items-center gap-3 p-4 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Send className="w-5 h-5 text-primary group-hover:text-white transition-colors" /></div>
                  <div className="min-w-0 flex-1 text-left"><h3 className="text-sm font-semibold">Reply — Imports</h3><p className="text-xs opacity-80">Client moving to Cayman</p></div>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin/email-composer?template=enquiry-reply-exports')}
                  data-testid="benefits-send-reply-exports"
                  className="flex items-center gap-3 p-4 border-2 border-purple-500 text-purple-700 rounded-lg hover:bg-purple-500 hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0"><Send className="w-5 h-5 text-purple-700 group-hover:text-white transition-colors" /></div>
                  <div className="min-w-0 flex-1 text-left"><h3 className="text-sm font-semibold">Reply — Exports</h3><p className="text-xs opacity-80">Client leaving Cayman</p></div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <section className="mb-6 border-y border-teal-200 bg-white px-5 py-5 sm:mb-8 sm:px-6" data-testid="meeting-summaries-pane">
          <h2 className="mb-4 text-base font-bold text-teal-800 md:text-lg" data-testid="meeting-dashboard-heading">Meeting Summaries</h2>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => navigate('/admin/meeting-summaries/new')} data-testid="meeting-dashboard-new" className="flex items-center gap-2 rounded-lg border border-teal-600 px-4 py-3 text-sm font-semibold text-teal-800 transition-colors hover:bg-teal-50"><ClipboardCheck size={18} />New meeting summary<ArrowRight size={16} /></button>
            <button type="button" onClick={() => navigate('/admin/meeting-summaries')} data-testid="meeting-dashboard-manage" className="flex items-center gap-2 rounded-lg border border-border px-4 py-3 text-sm font-semibold transition-colors hover:border-teal-600 hover:bg-teal-50"><FileText size={18} />All meeting summaries<ArrowRight size={16} /></button>
          </div>
        </section>
        {/* Consultation Summaries — post-call recap PDFs + email */}
        <section className="mb-6 border-y border-primary/20 bg-white px-5 py-5 sm:mb-8 sm:px-6" data-testid="service-flyers-pane">
          <h2 className="mb-3 text-base font-bold text-primary">Welcome &amp; Concierge flyers</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {['welcome', 'concierge'].map((slug) => <button key={slug} type="button" onClick={() => navigate(`/admin/flyers/${slug}`)} data-testid={`open-${slug}-flyer`}
              className="group flex items-center gap-3 rounded-lg border border-border px-4 py-3 text-left transition-colors hover:border-primary hover:bg-primary/5">
              <FileText className="h-5 w-5 shrink-0 text-primary" /><span className="flex-1 text-sm font-semibold">Pawsome {slug === 'welcome' ? 'Welcome' : 'Concierge'}</span><ArrowRight className="h-4 w-4 text-primary" />
            </button>)}
          </div>
        </section>

        <div
          className="bg-gradient-to-br from-teal-50 to-white rounded-xl border-2 border-primary/30 shadow-sm mb-6 sm:mb-8 overflow-hidden"
          data-testid="consultation-summaries-pane"
        >
          <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-primary/10 flex items-center gap-3">
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-foreground">Consultation Summaries</h2>
              <p className="text-xs sm:text-sm text-foreground/60">
                Recap post-consultation calls — discussion points, next steps &amp; timelines · on-brand PDF via email
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 sm:p-5">
            <button
              type="button"
              onClick={() => navigate('/admin/consultation-summaries/new')}
              data-testid="consult-new"
              className="flex items-center gap-3 p-4 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Plane className="w-5 h-5 text-primary group-hover:text-white transition-colors" /></div>
              <div className="min-w-0 flex-1 text-left"><h3 className="text-sm sm:text-base font-semibold">New summary</h3><p className="text-xs opacity-80">Start a fresh post-call recap</p></div>
              <ArrowRight className="w-4 h-4 opacity-60" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/consultation-summaries')}
              data-testid="consult-manage"
              className="flex items-center gap-3 p-4 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors group bg-white"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-primary" /></div>
              <div className="min-w-0 flex-1 text-left"><h3 className="text-sm sm:text-base font-semibold">Manage summaries</h3><p className="text-xs opacity-80">View, edit, duplicate or resend</p></div>
              <ArrowRight className="w-4 h-4 text-foreground/40" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/email-composer')}
              data-testid="consult-compose"
              className="flex items-center gap-3 p-4 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors group bg-white"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-primary" /></div>
              <div className="min-w-0 flex-1 text-left"><h3 className="text-sm sm:text-base font-semibold">Attach in composer</h3><p className="text-xs opacity-80">Pick any saved summary as an attachment</p></div>
              <ArrowRight className="w-4 h-4 text-foreground/40" />
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-border shadow-sm">
          <h2 className="text-base sm:text-xl font-bold text-foreground mb-3 sm:mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
            <button
              onClick={() => navigate('/admin/quotes/new')}
              className="p-3 sm:p-4 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-left"
            >
              <FileCheck className="w-6 h-6 sm:w-8 sm:h-8 mb-1.5 sm:mb-2" />
              <h3 className="font-semibold text-sm sm:text-base">Create Quote</h3>
              <p className="text-xs sm:text-sm text-foreground/60 hidden sm:block">Generate a new quote</p>
            </button>

            <button
              onClick={() => navigate('/admin/invoices/new')}
              className="p-3 sm:p-4 border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors text-left"
            >
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 mb-1.5 sm:mb-2" />
              <h3 className="font-semibold text-sm sm:text-base">Create Invoice</h3>
              <p className="text-xs sm:text-sm text-foreground/60 hidden sm:block">Generate a new invoice</p>
            </button>

            <button
              onClick={() => navigate('/admin/quotes')}
              className="p-3 sm:p-4 border-2 border-border rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <FileCheck className="w-6 h-6 sm:w-8 sm:h-8 mb-1.5 sm:mb-2 text-foreground/70" />
              <h3 className="font-semibold text-sm sm:text-base text-foreground">View Quotes</h3>
              <p className="text-xs sm:text-sm text-foreground/60 hidden sm:block">Manage all quotes</p>
            </button>

            <button
              onClick={() => navigate('/admin/invoices')}
              className="p-3 sm:p-4 border-2 border-border rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 mb-1.5 sm:mb-2 text-foreground/70" />
              <h3 className="font-semibold text-sm sm:text-base text-foreground">View Invoices</h3>
              <p className="text-xs sm:text-sm text-foreground/60 hidden sm:block">Manage all invoices</p>
            </button>

            <button
              onClick={() => navigate('/admin/receipts')}
              className="p-3 sm:p-4 border-2 border-border rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <Receipt className="w-6 h-6 sm:w-8 sm:h-8 mb-1.5 sm:mb-2 text-green-600" />
              <h3 className="font-semibold text-sm sm:text-base text-foreground">View Receipts</h3>
              <p className="text-xs sm:text-sm text-foreground/60 hidden sm:block">Payment confirmations</p>
            </button>

            <button
              onClick={() => navigate('/admin/clients')}
              className="p-3 sm:p-4 border-2 border-border rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <Users className="w-6 h-6 sm:w-8 sm:h-8 mb-1.5 sm:mb-2 text-blue-600" />
              <h3 className="font-semibold text-sm sm:text-base text-foreground">Manage Clients</h3>
              <p className="text-xs sm:text-sm text-foreground/60 hidden sm:block">Client accounts</p>
            </button>
          </div>
        </div>

        {/* Client Portal & Relocations Section */}
        <div className="mt-4 sm:mt-6 bg-white rounded-xl p-4 sm:p-6 border border-border shadow-sm">
          <h2 className="text-base sm:text-xl font-bold text-foreground mb-3 sm:mb-4">Client Portal Management</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
            <button
              onClick={() => navigate('/admin/tracker')}
              className="p-3 sm:p-6 border-2 border-cyan-500 text-cyan-600 rounded-lg hover:bg-cyan-50 transition-colors text-left"
            >
              <Calendar className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">🗓️ Pet Relocation Tracker</h3>
              <p className="text-xs sm:text-sm text-foreground/60 hidden sm:block">Calendar, schedule & document tracking dashboard</p>
            </button>

            <button
              onClick={() => navigate('/admin/relocations')}
              className="p-3 sm:p-6 border-2 border-teal-500 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors text-left"
            >
              <Plane className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Pet Relocations</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Track and manage pet relocation journeys</p>
            </button>

            <button
              onClick={() => navigate('/admin/clients')}
              className="p-3 sm:p-6 border-2 border-purple-500 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-left"
            >
              <Users className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Client Accounts</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Create and manage client portal access</p>
            </button>

            <button
              onClick={() => navigate('/admin/pets')}
              data-testid="nav-pets"
              className="p-3 sm:p-6 border-2 border-amber-500 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors text-left"
            >
              <PawPrint className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Pet Filing</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Permanent pet folders &amp; documents</p>
            </button>

            <button
              onClick={() => navigate('/admin/email-composer')}
              data-testid="nav-compose-email"
              className="p-3 sm:p-6 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-left"
            >
              <Send className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Compose Email</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Send with certificates, quotes &amp; more attached</p>
            </button>

            <button onClick={() => navigate('/admin/email-drafts')} data-testid="nav-email-drafts" className="p-3 sm:p-6 border-2 border-teal-600 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors text-left">
              <Mail className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Drafts &amp; Scheduled</h3>
            </button>

            <button
              onClick={() => navigate('/admin/emails')}
              data-testid="nav-email-activity"
              className="p-3 sm:p-6 border-2 border-sky-500 text-sky-600 rounded-lg hover:bg-sky-50 transition-colors text-left"
            >
              <Mail className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Email Activity</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Audit every email sent to clients</p>
            </button>

            <button
              onClick={() => navigate('/admin/library')}
              data-testid="nav-file-library"
              className="p-3 sm:p-6 border-2 border-amber-500 text-amber-600 rounded-lg hover:bg-amber-50 transition-colors text-left"
            >
              <Folder className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">File Library</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Bulk-upload · folders · attach in emails</p>
            </button>

            <button
              onClick={() => navigate('/admin/dictionary')}
              data-testid="nav-dictionary"
              className="p-3 sm:p-6 border-2 border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors text-left"
            >
              <BookOpen className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Custom Dictionary</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Never flag your domain words (FAVN, breeds, airline codes)</p>
            </button>

            <button
              onClick={() => navigate('/admin/analytics')}
              data-testid="nav-analytics"
              className="p-3 sm:p-6 border-2 border-teal-500 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors text-left"
            >
              <BarChart3 className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Analytics &amp; Finance</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">KPIs, cashflow, P&amp;L, CSV exports</p>
            </button>

            <button
              onClick={() => navigate('/admin/pay-cycles')}
              data-testid="nav-pay-cycles"
              className="p-3 sm:p-6 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors text-left"
            >
              <Calendar className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Pay Cycles</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Revenue per pay cycle (16th–15th)</p>
            </button>

            <button
              onClick={() => navigate('/admin/health-certificates')}
              data-testid="nav-health-certs"
              className="p-3 sm:p-6 border-2 border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors text-left"
            >
              <FileCheck className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Health Certificates</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Fillable export template · email to third parties</p>
            </button>

            <button
              onClick={() => navigate('/admin/refunds')}
              data-testid="nav-refunds"
              className="p-3 sm:p-6 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-left"
            >
              <RefreshCcw className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Refunds</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Issue refunds · audit log · revenue impact</p>
            </button>

            <button
              onClick={() => navigate('/admin/documents')}
              className="p-3 sm:p-6 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-left"
            >
              <Upload className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Documents</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Review and email client documents</p>
            </button>

            <button
              onClick={() => navigate('/admin/readiness-reports')}
              className="p-3 sm:p-6 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition-colors text-left"
            >
              <ClipboardCheck className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">TravelSafe Reports</h3>
              <p className="text-sm text-foreground/60">Generate pet travel readiness assessments</p>
            </button>

            <button
              onClick={() => navigate('/admin/content')}
              className="p-3 sm:p-6 border-2 border-orange-500 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors text-left"
            >
              <Settings className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Content Management</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Manage testimonials, partners, and services</p>
            </button>

            <button
              onClick={() => navigate('/admin/settings')}
              data-testid="dashboard-settings-tile"
              className="p-3 sm:p-6 border-2 border-slate-500 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-left"
            >
              <Sliders className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Settings</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Card fee note & other global toggles</p>
            </button>

            <button
              onClick={() => navigate('/admin/todos')}
              data-testid="dashboard-todos-tile"
              className="p-3 sm:p-6 border-2 border-teal-500 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors text-left"
            >
              <ListChecks className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">To-Do List</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Personal task tracker — cross off as you go</p>
            </button>

            <button
              onClick={() => navigate('/admin/price-list')}
              data-testid="dashboard-price-list-tile"
              className="p-3 sm:p-6 border-2 border-emerald-500 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors text-left"
            >
              <DollarSign className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Price List</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Edit prices once — appears everywhere instantly</p>
            </button>

            <button
              onClick={() => navigate('/admin/microchip-verifications')}
              data-testid="dashboard-microchip-tile"
              className="p-3 sm:p-6 border-2 border-cyan-500 text-cyan-700 rounded-lg hover:bg-cyan-50 transition-colors text-left"
            >
              <FileCheck className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Microchip Verifications</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Issue signed verification certificates to clients</p>
            </button>

            <button
              onClick={() => navigate('/admin/rabies-vaccinations')}
              data-testid="dashboard-rabies-tile"
              className="p-3 sm:p-6 border-2 border-rose-500 text-rose-700 rounded-lg hover:bg-rose-50 transition-colors text-left"
            >
              <FileCheck className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Rabies Vaccinations</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Issue DOA-compliant rabies vaccination certificates</p>
            </button>

            <button
              onClick={() => navigate('/admin/intake')}
              data-testid="dashboard-intake-tile"
              className="p-3 sm:p-6 border-2 border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors text-left"
            >
              <FileCheck className="w-7 h-7 sm:w-10 sm:h-10 mb-1.5 sm:mb-3" />
              <h3 className="font-semibold text-sm sm:text-lg mb-0.5 sm:mb-1">Travel Intake</h3>
              <p className="text-sm text-foreground/60 hidden sm:block">Send clients a link to fill in travel details on their phone</p>
            </button>
          </div>
        </div>
      </div>
      <DataHealthSweepModal open={dataHealthOpen} onClose={() => { setDataHealthOpen(false); fetchStats(); }} />
      {cycleModalOpen && stats?.current_cycle_key && (
        <PayCycleDetailModal
          cycleKey={stats.current_cycle_key}
          cycleLabel={stats.current_cycle_label}
          onClose={() => setCycleModalOpen(false)}
          navigate={navigate}
        />
      )}
      {revenueModalWindow && (
        <RevenueDetailModal
          window={revenueModalWindow}
          title={revenueModalWindow === 'ytd' ? 'Revenue year-to-date' : 'All-time revenue'}
          onClose={() => setRevenueModalWindow(null)}
          navigate={navigate}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Follow-up list — reusable card body for both "not opened" and "not signed"
// stale-quote panels shown on the dashboard.
// ---------------------------------------------------------------------------
const ACCENT_CLASSES = {
  amber: {
    banner: 'from-amber-50 to-white border-amber-200',
    iconBg: 'bg-amber-500/10 text-amber-700',
    dot: 'text-amber-600',
    hover: 'hover:bg-amber-50/50',
  },
  rose: {
    banner: 'from-rose-50 to-white border-rose-200',
    iconBg: 'bg-rose-500/10 text-rose-700',
    dot: 'text-rose-600',
    hover: 'hover:bg-rose-50/50',
  },
};

const FollowUpList = ({ testId, title, subtitle, accent, Icon, items, navigate }) => {
  const a = ACCENT_CLASSES[accent] || ACCENT_CLASSES.amber;
  return (
    <div
      className={`bg-white rounded-xl border shadow-sm overflow-hidden ${a.banner.replace('from-', 'border-').split(' ')[1] || 'border-border'}`}
      data-testid={testId}
    >
      <div className={`px-5 py-4 border-b bg-gradient-to-r ${a.banner} flex items-start gap-3`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${a.iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-foreground">{title}</h3>
            <span
              className={`px-2 py-0.5 text-xs font-bold rounded-full ${a.iconBg}`}
              data-testid={`${testId}-count`}
            >
              {items.length}
            </span>
          </div>
          <p className="text-xs text-foreground/60 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-sm text-foreground/50">
          <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
          You're all caught up.
        </div>
      ) : (
        <ul className="divide-y divide-border max-h-80 overflow-y-auto">
          {items.map((q) => (
            <li key={q.quote_id}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/quotes/${q.quote_id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/admin/quotes/${q.quote_id}`); } }}
                data-testid={`${testId}-row-${q.quote_id}`}
                className={`w-full text-left px-5 py-3 flex items-center gap-3 cursor-pointer ${a.hover} transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40`}
              >
                <Bell className={`w-4 h-4 flex-shrink-0 ${a.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-primary text-sm truncate">{q.quote_number || '—'}</span>
                    <span className="text-xs text-foreground/60 truncate">{q.client_name}</span>
                  </div>
                  <div className="text-xs text-foreground/60 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span title={`Last sent ${q.last_sent_at}`}>
                      Emailed {q.days_since_sent}d ago
                    </span>
                    {q.open_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Eye className="w-3 h-3" /> {q.open_count}× opened
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-foreground/40">
                        <EyeOff className="w-3 h-3" /> unopened
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-mono text-foreground/70">
                    {q.currency}{Number(q.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/email-composer?to=${encodeURIComponent(q.client_email || '')}&subject=${encodeURIComponent(`Following up on quote ${q.quote_number || ''}`)}`);
                    }}
                    className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border border-primary/40 text-primary rounded hover:bg-primary/5"
                    data-testid={`${testId}-nudge-${q.quote_id}`}
                    title="Compose a follow-up email"
                  >
                    <Send className="w-3 h-3" />
                    Nudge
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};